// NetworkGraphWidget — renders the `dependency-network` envelope using
// @xyflow/react. Supports three layout modes (Precedence L→R, Force-
// directed, Hierarchy) plus a "Highlight critical" filter that dims
// non-critical nodes. Critical-path nodes + edges are rose. Selected
// node and graph-audit summary are pushed to the right pin pane.

import { useCallback, useMemo, useState } from "react";
import {
  ReactFlow,
  Background,
  Controls,
  type Edge,
  type Node,
  type NodeMouseHandler,
} from "@xyflow/react";
import dagre from "dagre";
import "@xyflow/react/dist/style.css";
import { useRightPane } from "../ShellContext.js";

interface NetworkNode {
  id?: string;
  label?: string;
  phase?: string;
  duration_days?: number;
  kind?: string;
}

interface NetworkEdge {
  source?: string;
  target?: string;
  kind?: string;
}

interface AuditPayload {
  node_count: number;
  edge_count: number;
  root_count: number;
  sink_count: number;
  cycle_count: number;
  orphan_count: number;
  dangling_count: number;
  roots: string[];
  sinks: string[];
}

interface NetworkPayload {
  nodes?: NetworkNode[];
  edges?: NetworkEdge[];
  critical_path_ids?: string[];
  audit?: AuditPayload;
}

interface Props {
  data: unknown;
}

type LayoutMode = "precedence" | "force" | "hierarchy";

const LAYOUTS: Array<{ id: LayoutMode; label: string }> = [
  { id: "precedence", label: "Precedence (L→R)" },
  { id: "force",      label: "Force-directed" },
  { id: "hierarchy",  label: "Hierarchy" },
];

function dagreLayout(nodes: Node[], edges: Edge[], rankdir: "LR" | "TB"): Node[] {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir, nodesep: 30, ranksep: 60 });
  nodes.forEach((n) => g.setNode(n.id, { width: 160, height: 44 }));
  edges.forEach((e) => g.setEdge(e.source, e.target));
  dagre.layout(g);
  return nodes.map((n) => {
    const pos = g.node(n.id);
    return { ...n, position: { x: pos.x - 80, y: pos.y - 22 } };
  });
}

function forceLayout(nodes: Node[], edges: Edge[]): Node[] {
  // Crude radial-ish placement based on dagre output then jittered, so we
  // don't need d3-force. Good enough for "isn't a hierarchy" feel.
  const base = dagreLayout(nodes, edges, "LR");
  return base.map((n, i) => ({
    ...n,
    position: {
      x: n.position.x + Math.sin(i * 0.9) * 60,
      y: n.position.y + Math.cos(i * 1.3) * 80,
    },
  }));
}

export function NetworkGraphWidget({ data }: Props) {
  const payload = (data ?? {}) as NetworkPayload;
  const rawNodes = payload.nodes ?? [];
  const rawEdges = payload.edges ?? [];
  const audit = payload.audit ?? null;
  const critical = useMemo(
    () => new Set(payload.critical_path_ids ?? []),
    [payload.critical_path_ids],
  );

  const [mode, setMode] = useState<LayoutMode>("precedence");
  const [highlightCritical, setHighlightCritical] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { nodes, edges } = useMemo(() => {
    const baseNodes: Node[] = rawNodes
      .filter((n) => n.id)
      .map((n) => {
        const isCritical = critical.has(String(n.id));
        const dimmed = highlightCritical && !isCritical;
        return {
          id: String(n.id),
          position: { x: 0, y: 0 },
          data: {
            label: (
              <div className="text-[11px] leading-tight">
                <div className="font-medium">{n.label ?? n.id}</div>
                {n.duration_days != null && (
                  <div className="text-slate-500">{n.duration_days}d</div>
                )}
              </div>
            ),
          },
          style: {
            background: isCritical ? "#fff1f2" : "#ffffff",
            border: isCritical ? "2px solid #e11d48" : "1px solid #cbd5e1",
            borderRadius: 6,
            padding: 6,
            opacity: dimmed ? 0.25 : 1,
          },
        };
      });

    const baseEdges: Edge[] = rawEdges
      .filter((e) => e.source && e.target)
      .map((e, i) => {
        const isCritical = critical.has(String(e.source)) && critical.has(String(e.target));
        const dimmed = highlightCritical && !isCritical;
        return {
          id: `e${i}`,
          source: String(e.source),
          target: String(e.target),
          animated: isCritical && !dimmed,
          style: isCritical
            ? { stroke: "#e11d48", strokeWidth: 2, opacity: dimmed ? 0.15 : 1 }
            : { stroke: "#94a3b8",                  opacity: dimmed ? 0.1  : 1 },
        };
      });

    const laid =
      mode === "precedence" ? dagreLayout(baseNodes, baseEdges, "LR") :
      mode === "hierarchy"  ? dagreLayout(baseNodes, baseEdges, "TB") :
                              forceLayout(baseNodes, baseEdges);
    return { nodes: laid, edges: baseEdges };
  }, [rawNodes, rawEdges, critical, mode, highlightCritical]);

  // Push the selected node + graph audit to the right pin pane.
  const selectedNode = selectedId
    ? rawNodes.find((n) => String(n.id) === selectedId) ?? null
    : null;
  const successors = useMemo(() => {
    if (!selectedId) return [];
    return rawEdges
      .filter((e) => String(e.source) === selectedId)
      .map((e) => String(e.target));
  }, [rawEdges, selectedId]);
  const predecessors = useMemo(() => {
    if (!selectedId) return [];
    return rawEdges
      .filter((e) => String(e.target) === selectedId)
      .map((e) => String(e.source));
  }, [rawEdges, selectedId]);

  useRightPane(
    useMemo(() => (
      <RightPane
        selectedId={selectedId}
        selected={selectedNode}
        onClear={() => setSelectedId(null)}
        predecessors={predecessors}
        successors={successors}
        critical={critical}
        audit={audit}
      />
    ), [selectedId, selectedNode, predecessors, successors, critical, audit]),
  );

  const handleNodeClick = useCallback<NodeMouseHandler>((_e, node) => {
    setSelectedId((curr) => (curr === node.id ? null : node.id));
  }, []);

  if (!nodes.length) {
    return (
      <div className="p-6 text-sm text-slate-500">
        No nodes to render. Expecting `nodes` and `edges` arrays in the envelope data.
      </div>
    );
  }

  const rootHint = audit ? audit.roots.join(", ") || "—" : "—";
  const sinkHint = audit ? audit.sinks.join(", ") || "—" : "—";

  return (
    <div className="flex h-full w-full flex-col bg-white">
      <div className="flex flex-shrink-0 flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2 text-xs">
        <div className="text-slate-600">
          {audit
            ? `${audit.node_count} nodes · ${audit.edge_count} edges · ${audit.root_count} root${audit.root_count === 1 ? "" : "s"} (${rootHint}) · ${audit.sink_count} sink${audit.sink_count === 1 ? "" : "s"} (${sinkHint})`
            : `${rawNodes.length} nodes · ${rawEdges.length} edges`}
        </div>
        <div className="flex items-center gap-1 rounded-md border border-slate-200 bg-white p-0.5">
          {LAYOUTS.map((m) => (
            <button key={m.id} type="button" onClick={() => setMode(m.id)}
                    className={
                      "rounded px-2 py-0.5 " +
                      (mode === m.id ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100")
                    }>{m.label}</button>
          ))}
          <button
            type="button"
            onClick={() => setHighlightCritical((v) => !v)}
            className={
              "ml-1 rounded px-2 py-0.5 " +
              (highlightCritical
                ? "bg-rose-100 text-rose-700 ring-1 ring-rose-300"
                : "text-slate-700 hover:bg-slate-100")
            }
          >Highlight critical</button>
        </div>
      </div>
      <div className="min-h-0 flex-1">
        <ReactFlow nodes={nodes} edges={edges} fitView onNodeClick={handleNodeClick}>
          <Background />
          <Controls showInteractive={false} />
        </ReactFlow>
      </div>
    </div>
  );
}

function RightPane({ selectedId, selected, onClear, predecessors, successors, critical, audit }: {
  selectedId: string | null;
  selected: NetworkNode | null;
  onClear: () => void;
  predecessors: string[];
  successors: string[];
  critical: Set<string>;
  audit: AuditPayload | null;
}) {
  return (
    <div className="space-y-4">
      <section>
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Selected node</div>
          {selectedId && (
            <button type="button" onClick={onClear}
                    className="text-[10px] text-slate-400 hover:text-slate-600">clear</button>
          )}
        </div>
        {selected ? (
          <div className="mt-2 rounded border border-slate-200 p-2 text-xs">
            <div className="font-medium">
              {selected.id} · {selected.label ?? selected.id}
            </div>
            <dl className="mt-2 grid grid-cols-[100px_minmax(0,1fr)] gap-y-1 text-[11px]">
              <dt className="text-slate-500">phase</dt><dd>{selected.phase ?? "—"}</dd>
              <dt className="text-slate-500">on critical</dt>
              <dd className={critical.has(String(selected.id)) ? "text-rose-600" : ""}>
                {critical.has(String(selected.id)) ? "yes" : "no"}
              </dd>
              <dt className="text-slate-500">duration</dt>
              <dd>{selected.duration_days != null ? `${selected.duration_days}d` : "—"}</dd>
              <dt className="text-slate-500">predecessors</dt>
              <dd>{predecessors.length ? predecessors.join(", ") : "—"}</dd>
              <dt className="text-slate-500">successors</dt>
              <dd>{successors.length ? successors.join(", ") : "—"}</dd>
              <dt className="text-slate-500">fan-in</dt><dd>{predecessors.length}</dd>
              <dt className="text-slate-500">fan-out</dt><dd>{successors.length}</dd>
            </dl>
          </div>
        ) : (
          <div className="mt-2 rounded border border-dashed border-slate-200 p-3 text-xs text-slate-500">
            Click any node in the graph to see its details.
          </div>
        )}
      </section>

      {audit && (
        <section>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Graph audit</div>
          <div className="mt-2 space-y-1 rounded border border-slate-200 p-2 text-xs">
            <AuditRow label="roots" value={audit.root_count} ok={audit.root_count >= 1} />
            <AuditRow label="sinks" value={audit.sink_count} ok={audit.sink_count >= 1} />
            <AuditRow label="cycles" value={audit.cycle_count} ok={audit.cycle_count === 0} />
            <AuditRow label="orphan tasks" value={audit.orphan_count} ok={audit.orphan_count === 0} />
            <AuditRow label="dangling refs" value={audit.dangling_count} ok={audit.dangling_count === 0} />
          </div>
        </section>
      )}
    </div>
  );
}

function AuditRow({ label, value, ok }: { label: string; value: number; ok: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-slate-600">{label}</span>
      <span className="flex items-center gap-2">
        <span className="font-mono">{value}</span>
        <span className={ok ? "text-emerald-600" : "text-rose-600"}>{ok ? "✓" : "✗"}</span>
      </span>
    </div>
  );
}
